import React from "react";
import { AbsoluteFill } from "remotion";

/**
 * Shared scaling wrapper used by BOTH the preview Player and the CLI
 * render entry. Lives here (not inside render-entry.tsx) so preview and
 * render apply the SAME transform — if the slider says 60% in preview, the
 * exported file is shrunk to 60%, and there's no per-context branch where
 * a future edit could make them diverge.
 *
 * Reads four magic props off `props`:
 *   __contentScale  — user-controlled shrink (slider, 0.3..1)
 *   __compWidth     — actual composition size we're rendering at
 *   __compHeight    — "
 *   __baseWidth     — template's authored native size
 *   __baseHeight    — "
 *
 * If composition aspect differs from base aspect (landscape → portrait,
 * etc.), an additional fit-scale is composed so the graphic stays
 * centered instead of overflowing or sticking to a corner.
 */
export type ScalingMagicProps = {
  __contentScale?: number;
  __baseWidth?: number;
  __baseHeight?: number;
  __compWidth?: number;
  __compHeight?: number;
};

export function withScaling<P extends Record<string, unknown>>(
  Inner: React.ComponentType<P>,
): React.FC<P & ScalingMagicProps> {
  const Wrapped: React.FC<P & ScalingMagicProps> = (props) => {
    const {
      __contentScale: scale,
      __baseWidth,
      __baseHeight,
      __compWidth,
      __compHeight,
      ...rest
    } = props;
    const contentScale =
      typeof scale === "number" && scale > 0 ? scale : 1;

    const compW = typeof __compWidth === "number" ? __compWidth : 0;
    const compH = typeof __compHeight === "number" ? __compHeight : 0;
    const baseW = typeof __baseWidth === "number" ? __baseWidth : compW;
    const baseH = typeof __baseHeight === "number" ? __baseHeight : compH;
    const compAspect = compW && compH ? compW / compH : 0;
    const baseAspect = baseW && baseH ? baseW / baseH : 0;
    const aspectDiffers =
      compAspect > 0 &&
      baseAspect > 0 &&
      Math.abs(compAspect - baseAspect) > 0.01;

    const fitScale = aspectDiffers
      ? Math.min(compW / baseW, compH / baseH)
      : 1;
    const totalScale = contentScale * fitScale;

    return (
      <AbsoluteFill
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            position: "relative",
            width: baseW || "100%",
            height: baseH || "100%",
            transform:
              totalScale === 1 ? undefined : `scale(${totalScale})`,
            transformOrigin: "center center",
          }}
        >
          <Inner {...(rest as unknown as P)} />
        </div>
      </AbsoluteFill>
    );
  };
  Wrapped.displayName = `withScaling(${Inner.displayName ?? Inner.name ?? "Component"})`;
  return Wrapped;
}
